import { useCallback, useEffect, useMemo, useRef } from 'react';

import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

import Box from '@mui/material/Box';

import { useGetDaasProjects } from '../../../hooks';
import { ApplicationSetAppNameTemplateSection } from './sections/ApplicationSetAppNameTemplateSection';
import { ApplicationSetBasicInfoSection } from './sections/ApplicationSetBasicInfoSection';
import { ApplicationSetClusterSection } from './sections/ApplicationSetClusterSection';
import { ApplicationSetEditBasicInfoSection } from './sections/ApplicationSetEditBasicInfoSection';
import { ApplicationSetGeneratorsSection } from './sections/ApplicationSetGeneratorsSection';
import { ApplicationSetSourceSection } from './sections/ApplicationSetSourceSection';
import { ApplicationSetSyncOptionsSection } from './sections/ApplicationSetSyncOptionsSection';
import { ApplicationSetTemplatePatchSection } from './sections/ApplicationSetTemplatePatchSection';
import { AutoNamespaceSection } from './sections/AutoNamespaceSection';
import { AutoSyncSection } from './sections/AutoSyncSection';
import { ImageUpdaterSection } from './sections/ImageUpdaterSection';
import { MetadataSection } from './sections/MetadataSection';
import { OwnerSection } from './sections/OwnerSection';
import { ProjectSection } from './sections/ProjectSection';
import { useStyles } from './styles';
import {
  APPLICATION_SET_DEFAULT_DATA,
  ApplicationSetBasicInfoValue,
  AutoCreateNamespaceConfig,
  AutoSyncConfig,
  Cluster,
  ClusterConfiguration,
  DaasApplicationSetInputData,
  DaasApplicationSetInputUiOptions,
  DEFAULT_GENERATORS_DATA,
  DEFAULT_IMAGE_UPDATER_CONFIG,
  DEFAULT_TEMPLATE_PATCH_DATA,
  ImageUpdaterConfig,
  KubernetesMetadata,
  SourceConfiguration,
  SyncOption,
} from './types';
import { serializeProjectEntity } from './utils';


const withSeededKeys = (
  data: DaasApplicationSetInputData,
): DaasApplicationSetInputData => ({
  ...data,
  generators: data.generators ?? DEFAULT_GENERATORS_DATA,
  templatePatch: data.templatePatch ?? DEFAULT_TEMPLATE_PATCH_DATA,
  syncPolicy: data.syncPolicy ?? APPLICATION_SET_DEFAULT_DATA.syncPolicy,
  applicationMetadata:
    data.applicationMetadata ??
    APPLICATION_SET_DEFAULT_DATA.applicationMetadata,
  imageUpdater: data.imageUpdater ?? DEFAULT_IMAGE_UPDATER_CONFIG,
});

export const DaasApplicationSetInput = (
  props: FieldExtensionComponentProps<
    DaasApplicationSetInputData,
    DaasApplicationSetInputUiOptions
  >,
) => {
  const { onChange, formData, uiSchema, rawErrors } = props;
  const { classes } = useStyles();
  const fillEditFormData = uiSchema?.['ui:options']?.fillEditFormData === true;

  // Stabilize rawErrors — Backstage creates a new array reference on every render
  // even when contents are unchanged (typically []). Without this, React.memo on
  // sections is defeated because every section receives a new errors prop each render.
  const stableErrorsRef = useRef<string[]>(rawErrors ?? []);
  const nextErrors = rawErrors ?? [];
  if (
    nextErrors.length !== stableErrorsRef.current.length ||
    nextErrors.some((e, i) => e !== stableErrorsRef.current[i])
  ) {
    stableErrorsRef.current = nextErrors;
  }
  const errors = stableErrorsRef.current;

  const data = formData ?? APPLICATION_SET_DEFAULT_DATA;
  const syncPolicy = useMemo(
    () => data.syncPolicy ?? APPLICATION_SET_DEFAULT_DATA.syncPolicy,
    [data.syncPolicy],
  );

  // Refs to latest values — allow stable callbacks without stale closures
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;
  const syncPolicyRef = useRef(syncPolicy);
  syncPolicyRef.current = syncPolicy;

  const { value: entities, loading } = useGetDaasProjects(false, true);
  const userProjects = useMemo(
    () => entities?.map(entity => serializeProjectEntity(entity)) || [],
    [entities],
  );
  const selectedProject = useMemo(
    () => userProjects.find(project => project.name === data.project),
    [userProjects, data.project],
  );
  const projectClusters: Cluster[] = selectedProject?.clusters ?? [];

  const updateData = useCallback(
    (patch: Partial<DaasApplicationSetInputData>) => {
      onChangeRef.current(withSeededKeys({ ...dataRef.current, ...patch }));
    },
    [],
  );

  const updateSyncPolicy = useCallback(
    (patch: Partial<DaasApplicationSetInputData['syncPolicy']>) => {
      onChangeRef.current(
        withSeededKeys({
          ...dataRef.current,
          syncPolicy: { ...syncPolicyRef.current, ...patch },
        }),
      );
    },
    [],
  );

  const handleProjectChange = useCallback(
    (project: string) => {
      updateData({ project, cluster: null, source: null });
    },
    [updateData],
  );

  const handleBasicInfoChange = useCallback(
    (v: ApplicationSetBasicInfoValue) => {
      updateData(v);
    },
    [updateData],
  );

  const handleEditBasicInfoChange = useCallback(
    (
      v: Pick<
        DaasApplicationSetInputData,
        'applicationSetName' | 'description' | 'applicationSetRef'
      >,
    ) => {
      updateData(v);
    },
    [updateData],
  );

  // `_initialized` has to travel in formData: the scaffolder unmounts and
  // remounts field components as the user steps through the form, so a ref, a
  // context or sessionStorage would not survive to stop a second prefill from
  // discarding the user's edits.
  const handleApplicationSetEntitySelect = useCallback(
    (fullData: Partial<DaasApplicationSetInputData>) => {
      const sanitizedData = Object.fromEntries(
        Object.entries(fullData).filter(([, value]) => value !== undefined),
      ) as Partial<DaasApplicationSetInputData>;
      updateData({ ...sanitizedData, _initialized: true });
    },
    [updateData],
  );

  const handleOwnerChange = useCallback(
    (v: string) => {
      updateData({ owner: v });
    },
    [updateData],
  );

  const handleGeneratorsChange = useCallback(
    (v: string) => {
      updateData({ generators: v });
    },
    [updateData],
  );

  const handleAppNameTemplateChange = useCallback(
    (v: string) => {
      updateData({ appNameTemplate: v });
    },
    [updateData],
  );

  const handleClusterChange = useCallback(
    (v: ClusterConfiguration | null) => {
      updateData({ cluster: v });
    },
    [updateData],
  );

  const handleSourceChange = useCallback(
    (v: SourceConfiguration | null) => {
      updateData({ source: v });
    },
    [updateData],
  );

  const handleAutoSyncChange = useCallback(
    (v: AutoSyncConfig) => {
      updateSyncPolicy({ autoSync: v });
    },
    [updateSyncPolicy],
  );

  const handleAutoNamespaceChange = useCallback(
    (v: AutoCreateNamespaceConfig) => {
      updateSyncPolicy({ autoCreateNamespace: v });
    },
    [updateSyncPolicy],
  );

  const handleSyncOptionsChange = useCallback(
    (v: SyncOption[]) => {
      updateSyncPolicy({ syncOptions: v });
    },
    [updateSyncPolicy],
  );

  const handleMetadataChange = useCallback(
    (v: KubernetesMetadata) => {
      updateData({ applicationMetadata: v });
    },
    [updateData],
  );

  const handleTemplatePatchChange = useCallback(
    (v: string) => {
      updateData({ templatePatch: v });
    },
    [updateData],
  );

  const handleImageUpdaterChange = useCallback(
    (v: ImageUpdaterConfig) => {
      updateData({ imageUpdater: v });
    },
    [updateData],
  );

  // Initialize defaults once on mount — avoids a race where simultaneous child
  // effects with the same stale dataRef.current overwrite each other. syncPolicy
  // must be initialized here so downstream catalog actions always receive a
  // fully structured object.
  useEffect(() => {
    const d = dataRef.current;
    if (
      !d.generators ||
      !d.templatePatch ||
      !d.syncPolicy ||
      !d.applicationMetadata ||
      !d.imageUpdater
    ) {
      onChangeRef.current({
        ...d,
        generators: d.generators || DEFAULT_GENERATORS_DATA,
        templatePatch: d.templatePatch || DEFAULT_TEMPLATE_PATCH_DATA,
        syncPolicy: d.syncPolicy || APPLICATION_SET_DEFAULT_DATA.syncPolicy,
        applicationMetadata:
          d.applicationMetadata ||
          APPLICATION_SET_DEFAULT_DATA.applicationMetadata,
        imageUpdater: d.imageUpdater || DEFAULT_IMAGE_UPDATER_CONFIG,
      });
    }
  }, []);

  // Memoized value slices — sections only re-render when their own data changes
  const basicInfoValue = useMemo(
    () => ({
      applicationSetName: data.applicationSetName ?? '',
      description: data.description ?? '',
    }),
    [data.applicationSetName, data.description],
  );

  const editBasicInfoValue = useMemo(
    () => ({
      applicationSetName: data.applicationSetName ?? '',
      description: data.description ?? '',
      applicationSetRef: data.applicationSetRef,
    }),
    [data.applicationSetName, data.description, data.applicationSetRef],
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.mainContainer}>
        <ProjectSection
          value={data.project}
          projects={userProjects}
          loading={loading}
          onChange={handleProjectChange}
          errors={errors}
          disabled={fillEditFormData}
        />
        {fillEditFormData ? (
          <ApplicationSetEditBasicInfoSection
            value={editBasicInfoValue}
            onChange={handleEditBasicInfoChange}
            onApplicationSetEntitySelect={handleApplicationSetEntitySelect}
            isFormAlreadyPopulated={!!data._initialized}
            errors={errors}
          />
        ) : (
          <ApplicationSetBasicInfoSection
            value={basicInfoValue}
            selectedProject={data.project}
            onChange={handleBasicInfoChange}
            errors={errors}
          />
        )}
        {fillEditFormData && (
          <OwnerSection
            value={data.owner ?? ''}
            onChange={handleOwnerChange}
            errors={errors}
          />
        )}
        <ApplicationSetGeneratorsSection
          value={data.generators ?? ''}
          onChange={handleGeneratorsChange}
          errors={errors}
        />
        <ApplicationSetAppNameTemplateSection
          value={data.appNameTemplate ?? ''}
          onChange={handleAppNameTemplateChange}
          errors={errors}
        />
        <ApplicationSetClusterSection
          value={data.cluster}
          availableClusters={projectClusters}
          onChange={handleClusterChange}
          errors={errors}
          enableAppSetSupport
        />
        <ApplicationSetSourceSection
          value={data.source}
          onChange={handleSourceChange}
          errors={errors}
        />
        <AutoSyncSection
          value={syncPolicy.autoSync}
          onChange={handleAutoSyncChange}
        />
        <AutoNamespaceSection
          value={syncPolicy.autoCreateNamespace}
          onChange={handleAutoNamespaceChange}
          errors={errors}
          enableAppSetSupport
        />
        <ApplicationSetSyncOptionsSection
          value={syncPolicy.syncOptions}
          onChange={handleSyncOptionsChange}
          errors={errors}
          enableAppSetSupport
        />
        <MetadataSection
          value={data.applicationMetadata}
          onChange={handleMetadataChange}
          errors={errors}
          enableAppSetSupport
        />
        <ImageUpdaterSection
          value={data.imageUpdater ?? DEFAULT_IMAGE_UPDATER_CONFIG}
          onChange={handleImageUpdaterChange}
          source={data.source}
          errors={errors}
        />
        <ApplicationSetTemplatePatchSection
          value={data.templatePatch ?? ''}
          onChange={handleTemplatePatchChange}
          errors={errors}
        />
      </Box>
    </Box>
  );
};
